<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    left
    :close-on-content-click="false"
    max-width="420"
    min-width="380"
    max-height="600"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        v-bind="attrs"
        v-on="on"
        :aria-label="$t('notifications-center')"
        @click="onOpenMenu"
      >
        <v-badge
          :content="activeCount"
          :value="activeCount > 0"
          color="error"
          overlap
        >
          <v-icon>mdi-bell</v-icon>
        </v-badge>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="notification-center__header">
        <span class="text-h6">{{ $t('notifications-title') }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          small
          @click="menuOpen = false"
          :aria-label="$t('close')"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs v-model="activeTab" grow>
        <v-tab>{{ $t('notifications-active') }}</v-tab>
        <v-tab>{{ $t('notifications-history') }}</v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <div class="notification-center__content">
            <v-progress-linear
              v-if="loading"
              indeterminate
              color="primary"
            ></v-progress-linear>

            <div v-if="error" class="notification-center__error pa-4">
              <v-alert type="error" dense text>
                {{ error }}
              </v-alert>
            </div>

            <v-list v-if="!loading && activeNotifications.length > 0" class="py-0">
              <notification-item
                v-for="notification in activeNotifications"
                :key="notification.id"
                :notification="notification"
                :lastSeenAt="panelLastSeenAt"
                :dismissible="notification && (notification.isActive === true || notification.isActive === 'true' || notification.isActive === 1)"
                @dismiss="handleDismiss"
              />
            </v-list>

            <div v-if="!loading && activeNotifications.length === 0" class="notification-center__empty">
              <v-icon size="48" color="grey lighten-1">mdi-bell-off-outline</v-icon>
              <p class="mt-3 grey--text">{{ $t('notifications-no-active') }}</p>
            </div>
          </div>
        </v-tab-item>

        <v-tab-item>
          <div class="notification-center__content">
            <v-progress-linear
              v-if="loading"
              indeterminate
              color="primary"
            ></v-progress-linear>

            <div v-if="error" class="notification-center__error pa-4">
              <v-alert type="error" dense text>
                {{ error }}
              </v-alert>
            </div>

            <v-list v-if="!loading && historyNotifications.length > 0" class="py-0">
              <notification-item
                v-for="notification in historyNotifications"
                :key="notification.id"
                :notification="notification"
                :lastSeenAt="panelLastSeenAt"
                :dismissible="false"
                @dismiss="handleDismiss"
              />
            </v-list>

            <div v-if="!loading && historyNotifications.length === 0" class="notification-center__empty">
              <v-icon size="48" color="grey lighten-1">mdi-history</v-icon>
              <p class="mt-3 grey--text">{{ $t('notifications-no-history') }}</p>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-menu>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import NotificationItem from './notification-item.vue';

export default {
  name: 'NotificationCenter',
  components: {
    NotificationItem,
  },
  data() {
    return {
      menuOpen: false,
      activeTab: 0,
      panelLastSeenAt: null,
    };
  },
  computed: {
    ...mapGetters('notification', [
      'activeCount',
      'activeNotifications',
      'historyNotifications',
      'lastSeenAt',
      'loading',
      'error',
    ]),
  },
  methods: {
    ...mapActions('notification', [
      'initLastSeenAt',
      'fetchActiveNotifications',
      'fetchHistoryNotifications',
      'dismissNotification',
      'markAllSeen',
    ]),
    async onOpenMenu() {
      try {
        this.initLastSeenAt();

        // Snapshot lastSeenAt for this panel render so "NEW" highlights remain visible
        // during the first open. The store lastSeenAt will be updated for future opens.
        this.panelLastSeenAt = this.lastSeenAt;
        try {
          await this.markAllSeen();
        } catch (e) {
          console.warn('mark-seen failed (ignored for UX):', e);
        }
        
        if (this.activeTab === 0) {
          await this.fetchActiveNotifications();
        } else {
          await this.fetchHistoryNotifications();
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    },
    async handleDismiss(notificationId) {
      try {
        await this.dismissNotification(notificationId);
        this.$toasted.show(this.$t('notifications-dismissed'), {
          position: 'bottom-right',
          className: ['toast-success'],
          duration: 2000,
        });
      } catch (error) {
        this.$toasted.show(this.$t('notifications-error-dismiss'), {
          position: 'bottom-right',
          className: ['toast-error'],
          duration: 3000,
        });
      }
    },
  },
  watch: {
    menuOpen(isOpen) {
      if (!isOpen) {
        this.panelLastSeenAt = null;
      }
    },
    activeTab(newTab) {
      if (this.menuOpen) {
        if (newTab === 0) {
          this.fetchActiveNotifications();
        } else {
          this.fetchHistoryNotifications();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-center__header {
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.notification-center__content {
  max-height: 480px;
  overflow-y: auto;
  position: relative;
}

.notification-center__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.notification-center__error {
  padding: 16px;
}
</style>
